import React, { useState } from 'react';
import axios from 'axios';
import { FEEDBACK_API } from '../config';

/**
 * FeedbackPanel
 * ─────────────────────────────────────────────────────────────────
 * End-of-journey feedback form. Three questions:
 *   1. Was it easy to use?        — thumbs up / down
 *   2. How valuable was the support?  — 1–5 stars
 *   3. What could be better?      — free text (always shown, optional)
 *
 * Props:
 *   guru            string  e.g. "Decision", "Strategy", "Blueprint", "General"
 *   promptSent      string  The user's challenge / input (for admin context)
 *   responseReceived string The AI response (for admin context)
 *   contextData     object  Any additional context (lens, canvas, etc.)
 *   onComplete      fn      Optional — called after successful submission
 *   title           string  Optional override for the panel heading
 */
export default function FeedbackPanel({
  guru,
  promptSent,
  responseReceived,
  contextData = null,
  onComplete,
  title = 'How did that go?',
}) {
  const [easeRating, setEaseRating]   = useState(null);   // null | 1 | 0
  const [valueRating, setValueRating] = useState(null);   // null | 1–5
  const [hoverStar, setHoverStar]     = useState(null);
  const [comment, setComment]         = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [error, setError]             = useState('');

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; }
  })();

  const canSubmit = easeRating !== null && valueRating !== null && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!user?.id) { setError('Please log in to submit feedback.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${FEEDBACK_API}/submit`, {
        userId: user.id,
        guru,
        promptSent,
        responseReceived,
        rating: easeRating,
        qualityRating: valueRating,
        comment: comment.trim() || null,
        contextData,
      });
      setSubmitted(true);
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Feedback error:', err);
      setError('Could not submit — please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{
        margin: '2rem 0 0',
        padding: '1.25rem 1.5rem',
        background: '#f0fdf8',
        border: '1px solid #a7f3d0',
        borderRadius: 12,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '1.25rem', marginBottom: '0.4rem' }}>✓</div>
        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#065f46' }}>
          Thank you — feedback received.
        </div>
        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.25rem' }}>
          It goes straight to the team building GuruOne.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      margin: '2rem 0 0',
      padding: '1.5rem',
      background: 'white',
      border: '1px solid #e8e8f0',
      borderRadius: 12,
      boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    }}>
      {/* Heading */}
      <div style={{
        fontSize: '0.92rem',
        fontWeight: 700,
        color: '#1a1a2e',
        marginBottom: '1.25rem',
      }}>
        {title}
      </div>

      {/* Q1 — ease of use */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#444', marginBottom: '0.6rem' }}>
          Was it easy to use?
        </div>
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          {[
            { value: 1, label: '👍  Yes', activeColor: '#1D9E75', activeBg: '#f0fdf8', activeBorder: '#a7f3d0' },
            { value: 0, label: '👎  Not really', activeColor: '#dc2626', activeBg: '#fef2f2', activeBorder: '#fecaca' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setEaseRating(opt.value)}
              style={{
                padding: '0.5rem 1.1rem',
                borderRadius: 8,
                border: `1.5px solid ${easeRating === opt.value ? opt.activeBorder : '#e0e0e0'}`,
                background: easeRating === opt.value ? opt.activeBg : 'white',
                color: easeRating === opt.value ? opt.activeColor : '#555',
                fontSize: '0.82rem',
                fontWeight: easeRating === opt.value ? 700 : 400,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.12s',
                width: 'auto',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Q2 — value rating (stars) */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#444', marginBottom: '0.6rem' }}>
          How valuable was the support?
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          {[1, 2, 3, 4, 5].map(n => {
            const filled = (hoverStar ?? valueRating) >= n;
            return (
              <button
                key={n}
                onClick={() => setValueRating(n)}
                onMouseEnter={() => setHoverStar(n)}
                onMouseLeave={() => setHoverStar(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.1rem 0.15rem',
                  fontSize: '1.5rem',
                  color: filled ? '#f59e0b' : '#d1d5db',
                  transition: 'color 0.1s',
                  lineHeight: 1,
                  width: 'auto',
                }}
                title={['', 'Not useful', 'Slightly useful', 'Useful', 'Very useful', 'Excellent'][n]}
              >
                ★
              </button>
            );
          })}
          {valueRating && (
            <span style={{ fontSize: '0.72rem', color: '#888', marginLeft: '0.4rem' }}>
              {['', 'Not useful', 'Slightly useful', 'Useful', 'Very useful', 'Excellent'][valueRating]}
            </span>
          )}
        </div>
      </div>

      {/* Q3 — free text */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#444', marginBottom: '0.6rem' }}>
          What could be better? <span style={{ fontWeight: 400, color: '#aaa' }}>(optional)</span>
        </div>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="e.g. I needed more specific actions, the reasoning wasn't clear, a particular section was confusing…"
          rows={3}
          style={{
            width: '100%',
            padding: '0.65rem 0.8rem',
            border: '1.5px solid #e0e0e0',
            borderRadius: 8,
            fontSize: '0.82rem',
            fontFamily: 'inherit',
            resize: 'vertical',
            boxSizing: 'border-box',
            outline: 'none',
            color: '#333',
            lineHeight: 1.5,
          }}
        />
      </div>

      {/* Error */}
      {error && (
        <div style={{
          fontSize: '0.78rem',
          color: '#dc2626',
          marginBottom: '0.75rem',
          background: '#fef2f2',
          padding: '0.5rem 0.75rem',
          borderRadius: 6,
          border: '1px solid #fecaca',
        }}>
          {error}
        </div>
      )}

      {/* Submit */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.72rem', color: '#bbb' }}>
          {!canSubmit && easeRating === null ? 'Select ease rating to continue' :
           !canSubmit && valueRating === null ? 'Select a star rating to continue' : ''}
        </span>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            padding: '0.6rem 1.4rem',
            borderRadius: 8,
            background: canSubmit ? '#1D9E75' : '#e0e0e0',
            color: canSubmit ? 'white' : '#aaa',
            border: 'none',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit',
            transition: 'all 0.12s',
            width: 'auto',
          }}
        >
          {submitting ? 'Sending…' : 'Send feedback'}
        </button>
      </div>
    </div>
  );
}
